import type { LookupUser, LookupUsers } from '@atlassian/ui-modifications-core/types';
import { FieldValueLookupError, isNotAutomaticId } from '@atlassian/ui-modifications-core/utils';

import { getValueLookupValuesDiff } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/utils/get-value-lookup-values-diff/index.tsx';
import { isNotEmptyString } from '@atlassian/ui-modifications-public-api-utils/is-not-empty-string';
import { multiValueLookup } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/utils/multi-value-lookup/index.tsx';

type InternalValue = {
	id: string;
	name: string;
	avatarUrl: string;
};

type PublicGetValue = {
	accountId: string;
};

type PublicSetValue = string;

export type PeoplePublicGetValue = PublicGetValue[];
export type PeoplePublicSetValue = PublicSetValue[];
export type PeopleInternalValue = InternalValue | InternalValue[];

export const peopleSetValueValidator = (value: unknown): value is PeoplePublicSetValue =>
	Array.isArray(value) &&
	// We should omit automatic id special value so it won't be picked up by this field
	// It is only applicable to the assignee field
	value.every((userId) => isNotEmptyString(userId) && isNotAutomaticId(userId));

export const peopleSetValueTransformer = (
	value: PeoplePublicSetValue,
	lookupValues?: LookupUsers,
): PeopleInternalValue => {
	if (!value.length) {
		return [];
	}
	const filteredLookupValues = multiValueLookup<LookupUser>(value, lookupValues, 'accountId');

	if (value.length !== filteredLookupValues.length) {
		const notFoundValues = getValueLookupValuesDiff(value, filteredLookupValues, 'accountId');

		throw new FieldValueLookupError(notFoundValues.join());
	}

	return filteredLookupValues.map(({ accountId: id, name, picture: avatarUrl }) => ({
		id,
		name,
		avatarUrl,
	}));
};

export const peopleGetValueTransformer = (value: PeopleInternalValue): PeoplePublicGetValue => {
	if (!value) {
		return [];
	}

	// can be an object in case of people field with isMulti: false
	const valueArray = Array.isArray(value) ? value : [value];

	return valueArray.map(({ id: accountId }) => ({ accountId }));
};
